import { FormEvent, useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail } from 'react-icons/fi';
import { BRAND } from '../constants/brand';

type Status = 'idle' | 'loading' | 'success' | 'error';

const Contact = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const accessKey = process.env.REACT_APP_SKAARAPPS_ACCESS_KEY;
    if (!accessKey) {
      setStatus('error');
      setErrorMsg('Form is not configured yet. Please try again later.');
      return;
    }

    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get('name') || '');
    const email = String(data.get('email') || '');
    const phone = String(data.get('phone') || '');
    const company = String(data.get('company') || '');
    const message = String(data.get('message') || '');

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `Inquiry from ${BRAND.name} website — ${name}`,
          from_name: name,
          name,
          email,
          phone,
          company,
          message,
        }),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }

      form.reset();
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 scroll-mt-24 bg-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-card">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 md:p-12 lg:p-14">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              >
                Tell us about your <span className="text-gradient">priorities</span>
              </motion.h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Share where you are today—upcoming initiatives, compliance needs, or operational
                bottlenecks. We will respond with next steps and a sensible path forward.
              </p>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-center gap-3">
                  <FiMail className="text-brand-cyan shrink-0" />
                  <a href={`mailto:${BRAND.contact.email}`} className="hover:text-brand-violet transition-colors">
                    {BRAND.contact.email}
                  </a>
                </li>
              </ul>
            </div>
            <div className="p-8 md:p-12 lg:p-14 border-t lg:border-t-0 lg:border-l border-slate-200 bg-surface-elevated/50">
              {status === 'success' ? (
                <div role="status" className="space-y-4">
                  <p className="font-semibold text-slate-900 text-lg">Message sent</p>
                  <p className="text-slate-600 leading-relaxed">
                    Thanks for reaching out. We will get back to you shortly.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="text-sm font-medium text-brand-violet hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={onSubmit}>
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-slate-500 mb-1.5">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      required
                      autoComplete="name"
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan/40"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-slate-500 mb-1.5">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan/40"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-medium text-slate-500 mb-1.5">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan/40"
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-xs font-medium text-slate-500 mb-1.5">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      required
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan/40"
                      placeholder="Organization"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-slate-500 mb-1.5">
                      How can we help?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan/40 resize-none"
                      placeholder="Brief context, timeline, and success criteria…"
                    />
                  </div>
                  {status === 'error' && errorMsg && (
                    <p className="text-sm text-red-600" role="alert">
                      {errorMsg}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full sm:w-auto px-8 py-3.5 rounded-full font-semibold bg-gradient-brand text-white shadow-brand hover:opacity-95 transition-opacity disabled:opacity-60"
                  >
                    {status === 'loading' ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
